import { useEffect, useRef } from "react";

import { parsePersistedState, type PersistedState, useStore } from "./store";

const WS_BASE = import.meta.env.VITE_WS_BASE ?? "ws://ws.devurigestand.be/ws";

const RECONNECT_INTERVAL = 2000;
const DEBOUNCE_MS = 100;

export const SyncEngine = ({ room }: { room: string }) => {
  const applyNetworkState = useStore((s) => s.applyNetworkState);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Version tracking — the server is the source of truth
  const serverVersionRef = useRef(0);
  // Bumped locally on each outbound send, reset to server version on ack/sync
  const localVersionRef = useRef(0);
  // True while we're applying a remote update to Zustand (suppresses outbound send)
  const applyingRemote = useRef(false);

  useEffect(() => {
    let disposed = false;

    const connect = () => {
      if (disposed) return;

      const ws = new WebSocket(`${WS_BASE}/${room}`);
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        console.log("[sync] connected");
      });

      ws.addEventListener("message", (event) => {
        const data = JSON.parse(event.data);

        if (data.type === "sync" || data.type === "update") {
          const v = data.version ?? 0;

          // Ignore updates we've already seen
          if (data.type === "update" && v <= serverVersionRef.current) return;

          serverVersionRef.current = v;
          localVersionRef.current = v;

          applyingRemote.current = true;
          applyNetworkState(parsePersistedState(data.state));
          applyingRemote.current = false;
        }

        if (data.type === "ack") {
          // Server confirmed our write — update version
          serverVersionRef.current = data.version;
          localVersionRef.current = data.version;
        }
      });

      ws.addEventListener("close", () => {
        if (!disposed) {
          console.log("[sync] disconnected, reconnecting…");
          reconnectTimer.current = setTimeout(connect, RECONNECT_INTERVAL);
        }
      });

      ws.addEventListener("error", () => {
        ws.close();
      });
    };

    connect();

    // Subscribe to local Zustand changes → debounced push to server
    const unsub = useStore.subscribe(() => {
      if (applyingRemote.current) return;
      if (wsRef.current?.readyState !== WebSocket.OPEN) return;

      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) return;

        const latest = useStore.getState();
        const payload: PersistedState = {
          players: latest.players,
          activities: latest.activities,
          matches: latest.matches,
          results: latest.results,
          bets: latest.bets,
          lastStatusEvent: latest.lastStatusEvent,
          statusHistory: latest.statusHistory,
          multiplierConfig: latest.multiplierConfig,
        };

        wsRef.current.send(
          JSON.stringify({
            type: "update",
            state: payload,
            version: serverVersionRef.current,
          }),
        );
      }, DEBOUNCE_MS);
    });

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer.current);
      clearTimeout(debounceTimer.current);
      unsub();
      wsRef.current?.close();
    };
  }, [applyNetworkState, room]);

  return null;
};
